import {isEmpty} from 'lodash';
import React, {FC, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {ClickEventHandler} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

interface StyledRadioButtonProps {
  $isFreeform: boolean;
  $isStandalone: boolean;
  $isSelected: boolean;
  $isFocused: boolean;
  $hasFreeformValue: boolean;
}

const StyledRadioButton = styled(ChromelessButton)<StyledRadioButtonProps>`
  display: grid;
  grid-template-areas: 'check label';
  gap: 10px;
  grid-template-columns: ${(p) => (p.$isStandalone ? 'none' : '16px 1fr')};
  align-items: center;

  border: 1px solid ${(p) => p.theme.alphas.black20};
  border-radius: 8px;
  min-width: ${(p) => (p.$isFreeform ? '246px' : 'fit-content')};
  height: 50px;
  background: ${(p) => p.theme.greys.white};

  padding: 16px;
  ${(p) =>
    p.$isFocused &&
    css`
      background: ${p.$isFreeform ? p.theme.palette.blue.shade10 : p.theme.greys.white};
      border: 2px solid ${p.theme.palette.blue.shade40};
      // Remove 1px of padding to account for new border size.
      padding: 15px;
    `}

  ${(p) => addSelectionStyles(p.$isSelected, p.$hasFreeformValue, p.theme)}
`;

function addSelectionStyles(isChecked: boolean, isFreeform: boolean, theme: UserTheme) {
  if (isChecked) {
    return css`
      background: ${theme.palette.blue.shade10};

      &:hover {
        background: ${theme.palette.blue.shade20};
      }
    `;
  }
  return css`
    &:hover {
      background: ${theme.greys.shade20};
    }
  `;
}

const StyledLabelDiv = styled.div`
  grid-area: label;
  font-weight: ${fontWeights.semibold};
  line-height: ${fontSizes.medium};
  font-size: ${fontSizes.medium};
`;

const StyledInput = styled.input`
  font-weight: ${fontWeights.semibold};
  line-height: ${fontSizes.medium};
  font-size: ${fontSizes.medium};
  padding: 0;

  height: 16px;
  border: none;
  width: 100%;
  background: inherit;

  &::placeholder {
    color: ${(p) => p.theme.greys.shade60};
  }
`;

const StyledTaskCircleDiv = styled.div<{$isCheckbox: boolean; $isFilled?: boolean}>`
  background-color: ${(p) => p.theme.background};
  border-radius: ${(p) => (p.$isCheckbox ? '3px' : '50%')};
  border: 1.5px solid ${(p) => p.theme.greys.shade70};
  color: ${(p) => p.theme.greys.shade70};

  ${(p) =>
    p.$isFilled &&
    css`
      background-color: ${p.theme.palette.blue.shade40};
      border-color: ${p.theme.palette.blue.shade40};
    `}

  width: 16px;
  height: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCheckMarkContainer = styled.div`
  grid-area: check;
`;

interface OnboardingSelectionButtonBaseProps {
  type: 'radio' | 'checkbox';
  isChecked: boolean;
  onClick: ClickEventHandler;
  onClickCheckMark?: ClickEventHandler;
  label: ReactNode;

  isFreeform?: boolean;
  value?: string;
  onChangeValue?: (value: string) => void;

  isStandalone?: boolean;
  placeholder?: string;
}

interface OnboardingFreeformButtonProps {}

export type OnboardingSelectionButtonProps = OnboardingSelectionButtonBaseProps &
  (OnboardingFreeformButtonProps | {isFreeform?: false; value: undefined; onChangeValue: undefined});

export const OnboardingSelectionButton: FC<OnboardingSelectionButtonProps> = (props) => {
  const {isChecked, onClick, label, isStandalone, placeholder, onClickCheckMark} = props;
  const [isFocused, setIsFocused] = useState(false);
  const theme = useUserTheme();

  const isCheckbox = props.type === 'checkbox';
  const isFreeform = Boolean(props.isFreeform);
  const shouldShowCheck = isFreeform ? isChecked && !isEmpty(props.value) : isChecked;

  return (
    <StyledRadioButton
      onClick={onClick}
      $isFreeform={Boolean(isFreeform)}
      $isStandalone={Boolean(isStandalone)}
      $isSelected={shouldShowCheck}
      $isFocused={isFocused}
      $hasFreeformValue={isFreeform && !isEmpty(props.value)}
    >
      {!isStandalone && (
        <StyledCheckMarkContainer onClick={onClickCheckMark}>
          {renderCheckedIndicator(theme, shouldShowCheck, isCheckbox)}
        </StyledCheckMarkContainer>
      )}
      {isFreeform ? (
        <StyledInput
          placeholder={placeholder ?? 'Enter something else'}
          value={props.value}
          onChange={(event) => {
            props.onChangeValue?.(event.target.value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      ) : (
        <StyledLabelDiv>{label}</StyledLabelDiv>
      )}
    </StyledRadioButton>
  );
};

function renderCheckedIndicator(theme: UserTheme, isChecked: boolean, isCheckbox: boolean) {
  if (!isChecked) {
    return <StyledTaskCircleDiv $isCheckbox={isCheckbox} />;
  }

  if (isCheckbox) {
    return (
      <StyledTaskCircleDiv $isCheckbox={isCheckbox} $isFilled>
        <Icon name="checkmarkBoxSmall" color={theme.greys.white} />
      </StyledTaskCircleDiv>
    );
  }

  return <Icon name="checkmarkCircleSmall" color={theme.palette.blue.shade40} />;
}
